import { createContext, useContext, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Sidebar, { SidebarNav } from "./Sidebar";
import ContextBar from "./ContextBar";
import { useAuth } from "../../lib/auth";
import { useExceptionFeed, type FeedState } from "../../lib/ws";
import { DateRangeProvider } from "../../lib/dateRange";
import AuraHUD from "../../features/triage/AuraHUD";
import CommandPalette from "../CommandPalette";
import ErrorBoundary from "../ErrorBoundary";

const FeedCtx = createContext<FeedState>({ connected: false, live: [] });
export const useFeed = () => useContext(FeedCtx);

export default function AppShell() {
  const { tenant } = useAuth();
  const feed = useExceptionFeed(tenant?.slug);
  const [auraOn, setAuraOn] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if (e.key === "Escape") {
        setPaletteOpen(false);
        setMobileNavOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <DateRangeProvider>
      <FeedCtx.Provider value={feed}>
        <div className="flex h-screen w-screen overflow-hidden">
          <Sidebar />
          <div className="flex min-w-0 flex-1 flex-col">
            <ContextBar
              connected={feed.connected}
              auraOn={auraOn}
              onToggleAura={() => setAuraOn((v) => !v)}
              onOpenSearch={() => setPaletteOpen(true)}
              onToggleNav={() => setMobileNavOpen((v) => !v)}
            />
            <main className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-5">
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </main>
          </div>

          {/* Mobile off-canvas navigation (below lg) */}
          <AnimatePresence>
            {mobileNavOpen && (
              <>
                <motion.div
                  className="fixed inset-0 z-[55] bg-black/50 backdrop-blur-sm lg:hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setMobileNavOpen(false)}
                />
                <motion.aside
                  className="fixed left-0 top-0 z-[56] flex h-screen w-[240px] max-w-[80vw] flex-col overflow-y-auto border-r border-white/10 bg-ocean-900 px-3 py-4 lg:hidden"
                  initial={{ x: "-100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "-100%" }}
                  transition={{ type: "tween", duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
                >
                  <SidebarNav onNavigate={() => setMobileNavOpen(false)} />
                </motion.aside>
              </>
            )}
          </AnimatePresence>

          {auraOn && <AuraHUD onClose={() => setAuraOn(false)} live={feed.live} />}
          <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        </div>
      </FeedCtx.Provider>
    </DateRangeProvider>
  );
}
